import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { EInitEventTypeCode } from '@core/enums/init-event-type.enum';
import { AppCapabilityService } from '@core/services/app-capability.service';
import {
  LucideListPlus,
  LucideShieldAlert,
  LucideTriangleAlert,
} from '@lucide/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { AppFocusTargetDirective } from '@shared/directives/app-focus-target.directive';

type CreateEventAction = 'choice' | 'sb' | 'nnr';

const EVENT_TYPE_BY_ACTION: Record<
  CreateEventAction,
  EInitEventTypeCode | null
> = {
  choice: null,
  sb: EInitEventTypeCode.SB,
  nnr: EInitEventTypeCode.NNR,
};

export interface CreateEventFabRequest {
  readonly initialEventType: EInitEventTypeCode | null;
  readonly restoreFocus: HTMLElement;
}

@Component({
  selector: 'app-create-event-fab',
  imports: [
    LucideListPlus,
    LucideShieldAlert,
    LucideTriangleAlert,
    TranslatePipe,
    AppFocusTargetDirective,
  ],
  templateUrl: './create-event-fab.component.html',
  styleUrl: './create-event-fab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEventFabComponent {
  private readonly capabilities = inject(AppCapabilityService);

  public readonly createRequested = output<CreateEventFabRequest>();

  protected readonly action = computed<CreateEventAction | null>(() => {
    const canCreateSb = this.capabilities.canCreateSb();
    const canCreateNnr = this.capabilities.canCreateNnr();

    if (canCreateSb && canCreateNnr) {
      return 'choice';
    }
    if (canCreateSb) {
      return 'sb';
    }
    if (canCreateNnr) {
      return 'nnr';
    }
    return null;
  });

  protected readonly labelKey = computed(() => {
    switch (this.action()) {
      case 'sb':
        return 'CREATE_EVENT_FAB.CREATE_SB';
      case 'nnr':
        return 'CREATE_EVENT_FAB.CREATE_NNR';
      default:
        return 'CREATE_EVENT_FAB.CREATE_EVENT';
    }
  });

  protected open(event: MouseEvent): void {
    const action = this.action();
    const restoreFocus = event.currentTarget;
    if (action === null || !(restoreFocus instanceof HTMLElement)) {
      return;
    }

    this.createRequested.emit({
      initialEventType: EVENT_TYPE_BY_ACTION[action],
      restoreFocus,
    });
  }
}
